import 'keen-slider/keen-slider.min.css';
import designTokens from '@customink/design-tokens/build/ci_light/js/designTokens';
import {ArrowDownIcon} from '@customink/pigment-react';
import {IconButton} from '@mui/material';
import {styled} from '@mui/material/styles';
import useResizeObserver from '@react-hook/resize-observer';
import {useKeenSlider} from 'keen-slider/react';
import PropTypes from 'prop-types';
import React, {useCallback, useRef, useState} from 'react';
import useBreakpoint from 'hooks/useBreakpoint';

const CarouselContainer = styled('div')(() => ({
  '.slider': {
    '.arrow-container': {
      '.arrow': {
        '&.disabled': {
          backgroundColor: 'transparent',
          boxShadow: 'none',
          cursor: 'default'
        },
        '&.left svg': {transform: 'rotate(90deg) !important;'},
        '&.right svg': {transform: 'rotate(-90deg);'},
        '&:active': {
          backgroundColor: designTokens.color.gray.active.original.value
        },
        '&:hover': {
          backgroundColor: '#f3f3f3'
        },
        backgroundColor: '#fff',
        boxShadow: '0 0.25rem 0.5rem rgba(0, 0, 0, 0.08)',
        fontSize: '1.25rem',
        height: '2rem',
        margin: '0 0.5rem',
        pointerEvents: 'auto',
        width: '2rem',
        zIndex: 1
      },
      alignItems: 'center',
      bottom: 0,
      display: 'flex',
      justifyContent: 'space-between',
      left: 0,
      position: 'absolute',
      right: 0,
      top: 0
    },
    '.keen-slider': {
      display: 'flex !important',
      height: '100%',
      overflow: 'hidden !important',
      position: 'relative',
      touchAction: 'pan-y',
      userSelect: 'none',
      WebkitTouchCallout: 'none'
    },
    '.keen-slider__slide': {
      minHeight: '100%',
      overflow: 'hidden',
      position: 'relative',
      width: '100%'
    },
    backgroundColor: 'transparent',
    height: '100%',
    overflow: 'hidden',
    position: 'relative',
    width: '100%'
  },
  display: 'flex',
  height: 'max-content',
  position: 'relative',
  width: '100%'
}));

const CarouselCardsView = ({children}) => {
  const breakpoint = useBreakpoint();
  const gap = breakpoint === 'sm' ? 8 : 16;
  const slidesPerView = 4;

  const [sliderLoaded, setSliderLoaded] = useState(false);
  const [keenSliderCallbackRef, instanceRef] = useKeenSlider(
    {
      created: () => {
        setSliderLoaded(true);
      },
      initial: 0,
      loop: true,
      mode: 'snap',
      slidesPerView,
      spacing: gap
    },
    [slidesPerView, gap]
  );

  const boundingBoxRef = useRef(null);
  useResizeObserver(boundingBoxRef, () => instanceRef?.refresh());

  const handleLeftClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (instanceRef && instanceRef.prev) {
        instanceRef.prev();
      }
    },
    [instanceRef]
  );

  const handleRightClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (instanceRef && instanceRef.next) {
        instanceRef.next();
      }
    },
    [instanceRef]
  );

  const showArrows = children.length >= 2;

  return (
    <CarouselContainer
      sx={{
        '.arrow': {
          '&::before': {
            bottom: '-0.75rem',
            content: '""',
            left: '-0.75rem',
            position: 'absolute',
            right: '-0.75rem',
            top: '-0.75rem',
            zIndex: 1
          },
          position: 'relative',
          top: 'unset'
        },
        '.keen-slider__slide': {
          '& .MuiCard-root:hover': {
            boxShadow: 'none !important'
          }
        },
        '.left': {
          left: '0.5rem'
        },
        '.right': {
          right: '0.5rem'
        },
        '.slider': {
          height: '25rem'
        },
        display: 'block',
        margin: 'auto',
        width: '100%'
      }}>
      <div className="slider" ref={boundingBoxRef}>
        {showArrows && sliderLoaded && (
          <div className="arrow-container">
            <IconButton
              disableRipple
              className="arrow left"
              onClick={handleLeftClick}>
              <ArrowDownIcon
                sx={{fill: designTokens.color.gray.slateDefault.value}}
                fontSize="inherit"
              />
            </IconButton>

            <IconButton
              disableRipple
              className="arrow right"
              onClick={handleRightClick}>
              <ArrowDownIcon
                sx={{fill: designTokens.color.gray.slateDefault.value}}
                fontSize="inherit"
              />
            </IconButton>
          </div>
        )}
        <div ref={keenSliderCallbackRef} className="keen-slider">
          {children.map((slide, i) => (
            <div
              key={slide.key}
              className={`keen-slider__slide slide-${i}`}
              style={{cursor: 'grab'}}>
              {slide}
            </div>
          ))}
        </div>
      </div>
    </CarouselContainer>
  );
};

CarouselCardsView.propTypes = {
  children: PropTypes.node.isRequired
};

export default CarouselCardsView;
